import React, { useState } from "react";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  CreditCard,
  Shield,
  Truck,
  RefreshCw,
} from "lucide-react";
import SellexxLogo from "./sellexx-logo";
import PhoneComponent from "./phone-contact";

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = "" }) => {
  const [showContact, setShowContact] = useState(false);
  return (
    <footer
      className={`bg-gradient-to-b from-slate-900 to-slate-950 text-gray-200 ${className}`}
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-3 py-6 sm:px-4 sm:py-12">
        <div className="space-y-6 sm:space-y-10 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Company Info - Mobile Optimized */}
          <div className="text-center sm:text-left lg:col-span-1">
            <div className="flex justify-center sm:justify-start mb-3">
              <SellexxLogo withText mono={true} className="h-10 sm:h-14" />
            </div>
            <p className="text-sm leading-relaxed mb-5 px-2 sm:px-0 text-gray-300 max-w-xs mx-auto sm:max-w-none sm:mx-0">
              Premium quality products crafted with care. Express yourself with
              our unique designs and sustainable materials.
            </p>

            {/* Social Media Icons - Improved mobile spacing */}
            <div className="flex justify-center sm:justify-start space-x-3">
              {[
                { icon: Facebook, href: "#", label: "Facebook" },
                { icon: Instagram, href: "#", label: "Instagram" },
                { icon: Twitter, href: "#", label: "Twitter" },
                { icon: Youtube, href: "#", label: "YouTube" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="p-2.5 rounded-full bg-slate-800 hover:bg-slate-700 hover:text-white transition-all duration-300 transform hover:scale-110 active:scale-95"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Compact mobile layout */}
          <div className="text-center sm:text-left">
            <h3 className="text-base font-semibold text-white mb-3 sm:text-xl sm:mb-6">
              Quick Links
            </h3>
            <div className="grid grid-cols-2 gap-y-2 gap-x-4 sm:block sm:space-y-4">
              {[
                { href: "/", text: "Home" },
                { href: "/search", text: "Search" },
                { href: "/category", text: "Categories" },
                { href: "#", text: "About Us" },
                { href: "#", text: "Contact" },
              ].map(({ href, text }) => (
                <a
                  key={text}
                  href={href}
                  className="block py-1.5 px-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800 rounded-md transition-all duration-200 sm:px-0 sm:hover:bg-transparent sm:text-base sm:rounded-none"
                >
                  {text}
                </a>
              ))}
            </div>
          </div>

          {/* Contact Info - Streamlined mobile layout */}
          <div className="lg:col-span-2">
            <h3 className="text-base font-semibold text-white mb-3 text-center sm:text-left sm:text-xl sm:mb-6">
              Contact Info
            </h3>

            {/* Mobile: Card-style layout, Desktop: Original layout */}
            {/* Contact Details - Expand / Collapse */}
            <div className="space-y-3">
              <div className="flex items-center justify-center sm:justify-start space-x-3">
                <div className="p-1.5 bg-slate-800 rounded-full sm:p-2">
                  <Phone size={14} className="sm:size-4" />
                </div>

                <button
                  onClick={() => setShowContact(!showContact)}
                  className="text-sm sm:text-base font-medium text-gray-300 hover:text-white transition-colors"
                >
                  {showContact
                    ? "Hide contact details"
                    : "Show contact details"}
                </button>
              </div>

              {/* Expandable content */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  showContact ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="pl-9 space-y-2 pt-2">
                  <PhoneComponent
                    country="India"
                    phone="+919620099375"
                    display="+91 96200 99375"
                    className="text-sm sm:text-base"
                  />

                  <PhoneComponent
                    country="Saudi Arabia"
                    phone="+966543321175"
                    display="+966 543 321 175"
                    className="text-sm sm:text-base"
                  />

                  <div className="flex items-start space-x-2 pt-1">
                    <Mail size={14} className="mt-1" />
                    <a
                      href="mailto:info@sellexx.com"
                      className="text-sm sm:text-base text-gray-300 hover:text-white hover:underline transition-colors"
                    >
                      info@sellexx.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Improved mobile spacing */}
      <div className="border-t border-gray-800 bg-black">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-4 sm:py-6">
          <div className="flex flex-col space-y-3 sm:space-y-4 md:flex-row md:justify-between md:items-center md:space-y-0">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <p className="text-xs text-gray-400 sm:text-sm">
                © 2025 Sellexx. All rights reserved.
              </p>
            </div>

            {/* Legal Links - Better mobile arrangement */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 sm:gap-x-6">
              {["privacy", "cookies"].map((page) => (
                <a
                  key={page}
                  href={`/legal/${page}`}
                  className="text-xs text-gray-400 hover:text-white transition-colors duration-200 py-1 px-2 hover:bg-slate-800 rounded sm:text-sm"
                >
                  {page === "privacy" ? "Privacy Policy" : "Cookie Policy"}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
